// Phone screens and bits for the game modes: prompt choice, write-your-own, guess who,
// Sabse Bekaar and teams.
import { useEffect, useState } from 'react';
import { BLANK, CARD_TEXT_MAX, checkWrittenPrompt } from '@shared/rules';
import type { PublicPlayer, RoomView, RoundView, TeamView } from '@shared/types';
import { PromptCard } from '../../components/Cards';
import { Avatar, Timer } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import type { RoomApi } from '../../lib/useRoom';

export function names(list: { name: string }[]) {
  if (list.length <= 2) return list.map((p) => p.name).join(' and ');
  return `${list.slice(0, -1).map((p) => p.name).join(', ')} and ${list.at(-1)!.name}`;
}

// ------------------------------------------------------------------ round tags

/** Banner for Sabse Bekaar rounds. `stage` picks the wording for the phase. */
export function WorstBanner({ stage }: { stage: 'play' | 'judge' | 'vote' | 'watch' }) {
  const line = {
    play: 'Play your WORST card. The most bekaar answer wins.',
    judge: 'Crown the WORST card this time.',
    vote: 'Vote for the WORST card this time.',
    watch: 'The worst answer wins this round.',
  }[stage];
  return (
    <div className="worst-banner" role="note">
      <strong>🥴 Sabse Bekaar round</strong>
      <span>{line}</span>
    </div>
  );
}

/** Small chips above the prompt: who wrote it. */
export function RoundTags({ round }: { round: RoundView }) {
  if (!round.writtenBy) return null;
  return (
    <div className="row" style={{ gap: 6 }}>
      <span className="chip magenta">✍️ Written by {round.writtenBy}</span>
    </div>
  );
}

// ------------------------------------------------------------------ teams

export function teamOf(view: RoomView, playerId: string | null | undefined): TeamView | undefined {
  const p = view.players.find((x) => x.id === playerId);
  return p?.team == null ? undefined : view.teams[p.team];
}

/** Team totals with each team's players, shown under the scorebar. */
export function TeamBar({ view, meId }: { view: RoomView; meId?: string | null }) {
  if (!view.teams.length) return null;
  const myTeam = teamOf(view, meId)?.index;
  return (
    <div className="team-bar" aria-label="Team scores">
      {view.teams.map((t) => (
        <div key={t.index} className={`team-pill team-${t.index} ${t.index === myTeam ? 'mine' : ''}`}>
          <span className="team-name">{t.name}</span>
          <span className="team-faces" aria-hidden="true">
            {t.playerIds.map((id) => view.players.find((p) => p.id === id)?.avatar).join('')}
          </span>
          <span className="team-score">{t.score}</span>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ choosing

export function Choosing({ room, seconds }: { room: RoomApi; seconds: number | null }) {
  const view = room.view!;
  const round = view.round!;
  const me = view.me!;
  const { toast } = usePrefs();
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isJudge = round.judgeId === me.id;
  const judge = view.players.find((p) => p.id === round.judgeId);
  const options = round.promptOptions ?? [];

  useEffect(() => setChosen(null), [round.number]);

  async function choose() {
    if (!chosen) return;
    setBusy(true);
    const err = await room.act('round:choose', { promptId: chosen });
    setBusy(false);
    if (err) toast(err, 'error');
  }

  return (
    <>
      <main className="game-main">
        {round.notice && <p className="notice">{round.notice}</p>}
        <div className="row between">
          <span className="status-line">{isJudge ? "👑 Pick this round's prompt" : `👑 ${judge?.name ?? 'The judge'} is picking the prompt`}</span>
          <Timer seconds={seconds} />
        </div>
        {round.worst && <WorstBanner stage={isJudge ? 'watch' : 'play'} />}
        <div className="prompt-options">
          {options.map((card, i) =>
            isJudge ? (
              <button
                key={card.id}
                type="button"
                className={`prompt-option ${chosen === card.id ? 'chosen' : ''}`}
                aria-pressed={chosen === card.id}
                onClick={() => setChosen(card.id)}
              >
                <PromptCard card={card} className={i === 0 ? 'pop-in' : 'pop-in delay'} />
              </button>
            ) : (
              <div key={card.id} className="prompt-option waiting">
                <PromptCard card={card} />
              </div>
            ),
          )}
        </div>
        {!isJudge && (
          <p className="muted center" style={{ margin: 0 }}>
            One of these is coming your way. Start thinking about your hand.
          </p>
        )}
      </main>
      {isJudge && (
        <div className="action-bar">
          <div className="inner">
            <button className="btn btn-primary btn-lg btn-block" disabled={!chosen || busy} onClick={choose}>
              {chosen ? 'Play this prompt' : 'Tap the one you like'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ writing

export function Writing({ room, seconds }: { room: RoomApi; seconds: number | null }) {
  const view = room.view!;
  const round = view.round!;
  const me = view.me!;
  const { toast } = usePrefs();
  const [text, setText] = useState('');
  const [suggest, setSuggest] = useState(false);
  const [busy, setBusy] = useState(false);
  const isWriter = round.writerId === me.id;
  const writer = view.players.find((p) => p.id === round.writerId);
  const checked = checkWrittenPrompt(text);
  const preview = checked.ok ? { id: 'preview', type: 'prompt' as const, text: checked.text, pick: checked.pick } : null;

  useEffect(() => {
    setText('');
    setSuggest(false);
  }, [round.number]);

  async function send(useDeck: boolean) {
    setBusy(true);
    const err = await room.act('round:write', useDeck ? { useDeck: true } : { text, suggest });
    setBusy(false);
    if (err) toast(err, 'error');
  }

  if (!isWriter) {
    return (
      <main className="game-main">
        {round.notice && <p className="notice">{round.notice}</p>}
        <div className="row between">
          <span className="status-line">✍️ Write-your-own round</span>
          <Timer seconds={seconds} />
        </div>
        {round.worst && <WorstBanner stage="play" />}
        <section className="panel stack center writing-wait">
          <div className="pen" aria-hidden="true">
            ✍️
          </div>
          <h2 style={{ margin: 0 }}>{writer?.name ?? 'Someone'} is writing this round's prompt</h2>
          <p className="muted" style={{ margin: 0 }}>
            Expect inside jokes. Your cards are ready when it lands.
          </p>
        </section>
      </main>
    );
  }

  return (
    <>
      <main className="game-main">
        {round.notice && <p className="notice">{round.notice}</p>}
        <div className="row between">
          <span className="status-line">✍️ Your turn to write the prompt</span>
          <Timer seconds={seconds} />
        </div>
        {round.worst && <WorstBanner stage="watch" />}
        <form
          className="panel stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (checked.ok) void send(false);
          }}
        >
          <p className="muted small" style={{ margin: 0 }}>
            Write a line with a blank ({BLANK}) for everyone to fill, or ask a question. Family gossip welcome.
          </p>
          <textarea
            className="input"
            autoFocus
            rows={3}
            maxLength={CARD_TEXT_MAX}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="At every family wedding, Mamaji always ___."
            aria-label="Your prompt"
          />
          <div className="row between">
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setText((t) => `${t.trimEnd()}${t.trim() ? ' ' : ''}${BLANK}`)}>
              + Add a blank
            </button>
            <span className="muted small">
              {text.length}/{CARD_TEXT_MAX}
            </span>
          </div>
          {text.trim() && !checked.ok && <p className="muted small" style={{ margin: 0 }}>{checked.error}</p>}
          {preview && <PromptCard card={preview} />}
          <label className="check small">
            <input type="checkbox" checked={suggest} onChange={(e) => setSuggest(e.target.checked)} />
            Also suggest my prompt for the official deck
          </label>
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => send(true)}>
            Can't think of one? Use a card from the deck
          </button>
        </form>
      </main>
      <div className="action-bar">
        <div className="inner">
          <button className="btn btn-primary btn-lg btn-block" disabled={!checked.ok || busy} onClick={() => send(false)}>
            {busy ? 'Sending...' : 'Use my prompt'}
          </button>
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ guessing

export function Guessing({ room, seconds }: { room: RoomApi; seconds: number | null }) {
  const view = room.view!;
  const round = view.round!;
  const me = view.me!;
  const { toast } = usePrefs();
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const winner = round.submissions.find((s) => s.isWinner);
  const iWon = round.submissions.some((s) => s.isWinner && s.mine);
  const isHost = me.id === view.hostId;
  const suspects = (round.suspects ?? []).filter((s) => s.id !== me.id);
  const waiting = view.players.filter((p) => p.connected && round.guessers.includes(p.id) && !p.done);
  const guessed = view.players.find((p) => p.id === me.guessedFor);

  useEffect(() => setPick(null), [round.number]);

  async function lockIn() {
    if (!pick) return;
    setBusy(true);
    const err = await room.act('round:guess', { playerId: pick });
    setBusy(false);
    if (err) toast(err, 'error');
  }

  let status: string;
  if (iWon) status = "That's your card! 🤫 Keep a straight face.";
  else if (me.canGuess && !me.guessedFor) status = 'Who played the winning card?';
  else if (guessed) status = `You guessed ${guessed.name}. ${waiting.length ? `Waiting for ${names(waiting)}.` : ''}`;
  else status = `You sit this guess out. ${waiting.length ? `Waiting for ${names(waiting)}.` : ''}`;

  return (
    <>
      <main className="game-main">
        <div className="row between">
          <span className="status-line">🕵️ Guess who</span>
          <Timer seconds={seconds} />
        </div>
        <div className="winner-banner pop-in">
          <div>{round.worst ? '🥴 The most bekaar card' : '🏆 The winning card'}</div>
          <div className="who">...but whose is it?</div>
        </div>
        <PromptCard card={round.prompt} answers={winner?.cards?.map((c) => c.text)} />
        <p className="status-line center" style={{ margin: 0 }}>
          {status}
        </p>
        {me.canGuess && !me.guessedFor && (
          <div className="suspects" role="group" aria-label="Who played it?">
            {suspects.map((s) => {
              const player = view.players.find((p) => p.id === s.id);
              return (
                <button key={s.id} type="button" className="suspect" aria-pressed={pick === s.id} onClick={() => setPick(s.id)}>
                  {player && <Avatar player={player} />}
                  <span>{s.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </main>
      {((me.canGuess && !me.guessedFor) || isHost) && (
        <div className="action-bar">
          <div className="inner stack" style={{ gap: 8 }}>
            {me.canGuess && !me.guessedFor && (
              <button className="btn btn-primary btn-lg btn-block" disabled={!pick || busy} onClick={lockIn}>
                {pick ? `Lock in ${round.suspects?.find((s) => s.id === pick)?.name}` : 'Tap a name'}
              </button>
            )}
            {isHost && (me.guessedFor || !me.canGuess) && (
              <button
                className="btn btn-outline btn-block"
                onClick={async () => {
                  const err = await room.act('round:skip', {});
                  if (err) toast(err, 'error');
                }}
              >
                Reveal now
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/** Round-over line up of who guessed what. */
export function GuessResults({ view }: { view: RoomView }) {
  const results = view.round?.guesses;
  if (!results?.length) return null;
  const nameOf = (id: string) => view.players.find((p) => p.id === id)?.name ?? 'someone';
  const right = results.filter((g) => g.correct);
  return (
    <section className="panel stack guess-results" style={{ gap: 8 }}>
      <strong>
        🕵️ {right.length ? `${names(right.map((g) => ({ name: g.playerName })))} guessed right (+1)` : 'Nobody saw that coming!'}
      </strong>
      <ul>
        {results.map((g) => (
          <li key={g.playerId} className={g.correct ? 'right' : 'wrong'}>
            <span aria-hidden="true">{g.correct ? '✅' : '❌'}</span> {g.playerName} guessed {nameOf(g.suspectId)}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ------------------------------------------------------------------ game over

/** Team standings for the game-over screen. */
export function TeamStandings({ view }: { view: RoomView }) {
  if (!view.teams.length) return null;
  const ranked = [...view.teams].sort((a, b) => b.score - a.score);
  const top = ranked[0].score;
  return (
    <section className="team-standings">
      {ranked.map((t) => (
        <div key={t.index} className={`team-card team-${t.index} ${t.score === top && top > 0 ? 'winner' : ''}`}>
          <div className="row between" style={{ flexWrap: 'nowrap' }}>
            <strong className="team-name">
              {t.score === top && top > 0 ? '🏆 ' : ''}
              {t.name}
            </strong>
            <span className="team-score">{t.score}</span>
          </div>
          <div className="muted small">{names(t.playerIds.map((id) => view.players.find((p) => p.id === id)).filter((p): p is PublicPlayer => Boolean(p)))}</div>
        </div>
      ))}
    </section>
  );
}

/** Headline winner(s): the top team when teams are on, else the top players. */
export function championNames(view: RoomView): string[] {
  if (view.teams.length) {
    const top = Math.max(0, ...view.teams.map((t) => t.score));
    return top > 0 ? view.teams.filter((t) => t.score === top).map((t) => t.name) : [];
  }
  const top = Math.max(0, ...view.players.map((p) => p.score));
  return top > 0 ? view.players.filter((p) => p.score === top).map((p) => p.name) : [];
}
